import { onCleanup } from 'solid-js';
import {
  type CorrectComponentType,
  customShadowlessElement,
  correctElementType,
} from '@brytdesigns/web-component-core/utils';
import { animate, inView, prefersReducedMotion, type DOMKeyframesDefinition } from 'motion';

import './motion-element.css';

type AnimationKind =
  | 'fade-in'
  | 'fade-up'
  | 'fade-down'
  | 'fade-left'
  | 'fade-right'
  | 'scale-up'
  | 'scale-down'
  | 'blur-in';

type EasingKind = 'linear' | 'ease-out' | 'ease-in-out' | 'back-out';

type Props = {
  dataAnimation: AnimationKind | '';
  dataDuration: number;
  dataDelay: number;
  dataEasing: EasingKind;
};

const KEYFRAMES: Record<AnimationKind, DOMKeyframesDefinition> = {
  'fade-in': { opacity: [0, 1] },
  'fade-up': { opacity: [0, 1], y: [20, 0] },
  'fade-down': { opacity: [0, 1], y: [-20, 0] },
  'fade-left': { opacity: [0, 1], x: [20, 0] },
  'fade-right': { opacity: [0, 1], x: [-20, 0] },
  'scale-up': { opacity: [0, 1], scale: [0.9, 1] },
  'scale-down': { opacity: [0, 1], scale: [1.05, 1] },
  'blur-in': { opacity: [0, 1], filter: ['blur(8px)', 'blur(0px)'] },
};

const EASING_MAP: Record<EasingKind, 'linear' | 'easeOut' | 'easeInOut' | 'backOut'> = {
  linear: 'linear',
  'ease-out': 'easeOut',
  'ease-in-out': 'easeInOut',
  'back-out': 'backOut',
};

const ROOT_MARGIN = '-40px 0px 0px 0px';
const COMPLETE_ATTR = 'data-animation-complete';

const MotionElement: CorrectComponentType<Props> = (props, { element }) => {
  if (!props.dataAnimation) return;

  const keyframes = KEYFRAMES[props.dataAnimation];
  if (!keyframes) return;

  if (prefersReducedMotion.current) {
    element.setAttribute(COMPLETE_ATTR, '');
    return;
  }

  const stop = inView(
    element,
    () => {
      const controls = animate(element, keyframes, {
        duration: props.dataDuration,
        delay: props.dataDelay,
        ease: EASING_MAP[props.dataEasing] ?? 'easeOut',
      });
      controls.finished.then(() => element.setAttribute(COMPLETE_ATTR, '')).catch(() => {});
    },
    { margin: ROOT_MARGIN },
  );

  onCleanup(() => stop());
};

customShadowlessElement(
  'motion-element',
  { dataAnimation: '', dataDuration: 0.6, dataDelay: 0, dataEasing: 'ease-out' },
  correctElementType(MotionElement),
);
